import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { CLAIM_TYPE_STYLE } from "@/components/claims/ClaimCard";

const VIEWERS = new Set(["back_office", "super_admin", "factory_1"]);
const fmtAt = (s) => (s ? s.slice(0, 10) : "—");

// Dashboard: open after-sale claims at a glance + folder counts.
export function ClaimsPanel({ user }) {
  const { t, lang } = useT();
  const { data } = useQuery({
    queryKey: ["claims", "open"],
    queryFn: async () => (await api.get("/claims?status=open")).data,
    enabled: VIEWERS.has(user?.role),
    refetchInterval: 60_000,
  });
  if (!VIEWERS.has(user?.role) || !data) return null;
  const claims = data.claims || [];
  const counts = data.counts || {};
  const pick = (c) => (lang === c.note_translation_lang && c.note_translation) ? c.note_translation : c.note;
  return (
    <section className="bg-white border border-red-200 rounded-md" data-testid="dashboard-claims">
      <header className="px-5 py-3 border-b border-red-100 bg-red-50 flex items-center justify-between rounded-t-md">
        <div>
          <h2 className="font-display text-lg font-semibold text-red-900">⚠️ {t("Claims")} <span className="font-normal text-sm text-red-700">· {t("After-sale QC")}</span></h2>
          <p className="text-xs text-red-700">
            {t("Warranty")} {counts.warranty || 0} · {t("Re-ordered")} {counts.reorder || 0} · {t("Refunds")} {counts.refund || 0} · {t("New logo")} {counts.new_logo || 0} · {t("Resolved")} {counts.resolved || 0}
          </p>
        </div>
        <Link to="/claims" data-testid="dashboard-claims-link">
          <Badge className={`${claims.length ? "bg-rose-600 text-white border-rose-700" : "bg-slate-100 text-slate-700 border-slate-200"} text-sm px-3 py-1`} data-testid="dashboard-claims-open-count">
            {claims.length} {t("open")}
          </Badge>
        </Link>
      </header>
      <ul>
        {claims.length === 0 && <li className="px-5 py-4 text-sm text-slate-500">{t("No open claims.")}</li>}
        {claims.slice(0, 6).map((c) => (
          <li key={c.id} className="px-5 py-2.5 border-b border-slate-100 last:border-b-0 flex flex-wrap items-center gap-2 text-sm" data-testid={`dashboard-claim-${c.id}`}>
            <Link to={`/order/${c.order_id}`} className="font-mono font-bold hover:underline">{c.po_number}</Link>
            <Link to={`/claims?claim=${c.id}`} className="text-[11px] underline text-rose-700" data-testid={`dashboard-claim-open-${c.id}`}>{t("claim")} ↗</Link>
            <Badge className={`border text-[10px] font-bold uppercase ${CLAIM_TYPE_STYLE[c.type] || ""}`}>{t(c.type_label)}</Badge>
            <span className="text-slate-700 truncate max-w-[520px]" title={c.note}>{pick(c)}</span>
            <span className="ml-auto text-[11px] font-mono text-slate-500">{c.filed_by} · {fmtAt(c.filed_at)}</span>
          </li>
        ))}
        {claims.length > 6 && <li className="px-5 py-2 text-xs text-slate-500"><Link to="/claims" className="underline">+{claims.length - 6} {t("more in Claims")}</Link></li>}
      </ul>
    </section>
  );
}
